<!-- ❗ if the feature flag is not enabled, a redirect to the homepage will occur (see routes.ts) -->
<template>
   <Head>
      <title>{{ $t("meta.contribution_external.title") }}</title>
      <meta name="description" :content="$t('meta.contribution_external.content')" />
   </Head>

   <PageLoader :isLoading="isLoading" :loadErrors="loadErrors">
      <div class="container mb-20 flex flex-col items-center" data-cy="contributions-feature">
         <!-- heading and text -->
         <div class="max-w-82 md:max-w-175 flex items-center justify-center py-12 text-center md:py-32">
            <div>
               <h1 class="h1 text-2xl text-lighter leading-normal text-brand-dark md:text-4xl md:leading-snug">
                  {{ $t("account.contributions.benefits_header") }}
               </h1>
               <p class="text-regular mt-8">{{ $t("account.contributions.benefits_paragraph") }}</p>
            </div>
         </div>

         <!-- Contributions table -->
         <section>
            <div v-if="userContributions.length > 0" data-cy="contributions-table">
               <!-- contributions table -->
               <div data-cy="user-contributions-headings" class="mb-2 text-grey-70">
                  <!-- larger screens - 'column' labels -->
                  <!-- table headings -->
                  <div class="hidden items-center px-12 text-sm leading-tight lg:flex">
                     <div class="w-1/2 shrink-0 pr-4">{{ $t("account.contributions.table_title") }}</div>
                     <div class="w-1/8 shrink-0 pr-4">{{ $t("account.contributions.table_type") }}</div>
                     <div class="w-1/8 shrink-0 pr-4">{{ $t("account.contributions.table_date") }}</div>
                     <div class="w-1/4 shrink-0 text-center md:pl-4">{{ $t("account.contributions.table_link") }}</div>
                  </div>
               </div>

               <!-- table data -->
               <div
                  v-for="(usrContribution, i) in userContributions"
                  :key="`contribution-${i}`"
                  class="lg:h-30 mb-4 flex h-full rounded-lg bg-brand-dark p-4 text-white lg:px-10 lg:py-5"
               >
                  <div class="lg:flex-no-wrap flex w-full flex-wrap items-center text-sm leading-tight">
                     <!-- title -->
                     <div
                        class="order-3 mt-4 w-full shrink-0 items-center pr-0 leading-tight lg:order-1 lg:mt-0 lg:flex lg:h-full lg:w-1/2 lg:pr-4"
                     >
                        <div
                           :data-cy="`user-contribution-${i}-title`"
                           class="contribution-title lg:max-h-full lg:overflow-y-scroll lg:py-2.5"
                        >
                           {{ usrContribution.title }}
                        </div>
                        <!-- bottom fade text for scrolling text -->
                        <div
                           class="contribution-title-transparency py-2.4 absolute bottom-0 left-0 hidden w-full lg:block"
                        ></div>
                     </div>
                     <!-- type -->
                     <div
                        :data-cy="`user-contribution-${i}-type`"
                        class="text-lighter order-1 w-1/2 shrink-0 pr-4 lg:order-2 lg:w-1/8"
                     >
                        <span class="lg-sr-only"></span>{{ usrContribution.type }}
                     </div>
                     <!--end date -->
                     <div
                        :data-cy="`user-contribution-${i}-end-date`"
                        class="text-lighter order-2 w-1/2 shrink-0 text-right lg:order-3 lg:w-1/8 lg:pr-4 lg:text-left"
                     >
                        <span v-if="usrContribution.endDate">{{ formattedDate(usrContribution.endDate) }}</span>
                        <span v-else>{{ $t("account.contributions.study_ongoing") }}</span>
                     </div>
                     <!--study link button -->
                     <div class="order-4 mt-4 w-full shrink-0 lg:mt-0 lg:w-1/4 lg:pl-4 lg:text-center">
                        <LinkButton
                           :data-cy="`user-contribution-${i}-link`"
                           :url="usrContribution.externalUrl"
                           :color="Theme.White"
                           roundedEdges="md"
                           isExternal
                           target="_blank"
                        >
                           {{ $t("account.contributions.button_visit_study") }}
                        </LinkButton>
                     </div>
                  </div>
               </div>
            </div>
            <!-- No external contributions -->
            <p v-else class="text-center" data-cy="no-contributions-message">
               {{ $t("account.contributions.no_contributions") }}
            </p>
         </section>
      </div>
   </PageLoader>
</template>

<script setup lang="ts">
import { Head } from "@unhead/vue/components";
import { Theme } from "@/types";
import ContributionsApi, { type ContributionResp } from "@/data/api-layer/contributionsApi";
import UserContributionsApi, { type UserContributionResp } from "@/data/api-layer/userContributionsApi";
import { useFormattedDate } from "@/composables/useFormattedDate";
import { useApiRequest } from "@/composables/useApiRequest";

const { isLoading, execManyRequests, loadErrors } = useApiRequest();
const { t: $t } = useI18n();

// data & requests
// -----------------------------------------
// API responses
const contributionsResp = ref<ContributionResp[]>([]);
const userContributionsResp = ref<UserContributionResp[]>([]);

// lifecycle
// -----------------------------------------
onMounted(async () => {
   const responses = await execManyRequests([() => ContributionsApi.getAll(), () => UserContributionsApi.getAll()]);
   if (loadErrors.value.length > 0) return;

   contributionsResp.value = responses[0].value;
   userContributionsResp.value = responses[1].value;
});

// computed
// -----------------------------------------
const userContributions = computed(() =>
   // filter all contribution
   contributionsResp.value.filter((contribution) =>
      // return only ones with matching id and contributionId
      userContributionsResp.value.some((userContribution) => {
         return userContribution.contributionId === contribution.id;
      })
   )
);

// methods
// -----------------------------------------
const formattedDate = (date) => {
   if (!date) return null;

   return useFormattedDate(date);
};
</script>

<style scoped lang="scss">
// we want the ability to scroll without show the OS scrollbar
.contribution-title {
   &::-webkit-scrollbar {
      width: 0px;
      background: transparent; /* make scrollbar transparent */
   }
}
.contribution-title-transparency {
   content: "";
   background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, theme("colors.brand-dark") 70%);
}
</style>
